/**
 * Xendit SDK utilities and type definitions
 */

export interface XenditCardData {
  card_number: string;
  expiry_month: string;
  expiry_year: string;
  cvn: string;
  cardholder_first_name?: string;
  cardholder_last_name?: string;
  cardholder_email?: string;
  cardholder_phone_number?: string;
  payment_session_id: string;
  save_card_payment_token?: boolean;
}

export interface XenditResponse {
  id?: string;
  status?: string;
  authentication_id?: string;
  payment_request_id?: string;
  [key: string]: any;
}

export interface XenditError {
  error_code?: string;
  message?: string;
  [key: string]: any;
}

/**
 * Check if Xendit SDK is loaded and ready
 */
export const isXenditAvailable = (): boolean => {
  return !!(
    typeof window !== 'undefined' &&
    window.Xendit &&
    window.Xendit.setPublishableKey &&
    window.Xendit.payment &&
    window.Xendit.payment.collectCardData
  );
};

export const list_payment_channels = {
    "QR_PAYMENT": [
        "PROMPTPAY",
    ],
    "E_WALLET": [
        "LINEPAY",
        "SHOPEEPAY",
        "TRUEMONEY",
    ],
    "MOBILE_BANKING": [
        'KBANK_MOBILE_BANKING',
        'KRUNGSRI_MOBILE_BANKING',
        'BBL_MOBILE_BANKING',
        'SCB_MOBILE_BANKING',
        'KTB_MOBILE_BANKING'
    ],
    "DIRECT_DEBIT": [
        "KRUNGSRI_DIRECT_DEBIT",
        "BBL_DIRECT_DEBIT",
        "KTB_DIRECT_DEBIT",
        "SCB_DIRECT_DEBIT"
    ]
};

/**
 * Initialize Xendit with public key and optional session ID
 */
export const initializeXendit = (publicKey: string, sessionId?: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!publicKey) {
      console.error('Xendit public key is required');
      resolve(false);
      return;
    }

    const checkAndInit = () => {
      if (isXenditAvailable()) {
        try {
          window.Xendit.setPublishableKey(publicKey);
          
          // If session ID is provided, set it up for Cards Session
          if (sessionId) {
            // Initialize Cards Session if session ID is provided
            if (window.Xendit.CardsSession) {
              window.Xendit.CardsSession.init({
                sessionId: sessionId
              });
            }
          }
          
          console.log('Xendit SDK initialized successfully');
          resolve(true);
        } catch (error) {
          console.error('Error initializing Xendit SDK:', error);
          resolve(false);
        }
      } else {
        // Wait and retry
        setTimeout(checkAndInit, 1000);
      }
    };

    checkAndInit();
  });
};

/**
 * Process card payment with Xendit
 */
export const processCardPayment = (
  cardData: XenditCardData
): Promise<{ error: XenditError | null; response: XenditResponse | null }> => {
  return new Promise((resolve) => {
    if (!isXenditAvailable()) {
      resolve({
        error: { message: 'Xendit SDK is not available' },
        response: null,
      });
      return;
    }

    // Check if we're in a browser environment and DOM is available
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve({
        error: { message: 'DOM is not available' },
        response: null,
      });
      return;
    }

    // Verify publishable_key is set before processing payment
    if (!window.Xendit.settings || !window.Xendit.settings.publishable_key) {
      console.error('❌ Xendit publishable_key is not set!');
      resolve({
        error: { message: 'Payment system configuration error. Please refresh the page and try again.' },
        response: null,
      });
      return;
    }

    try {
      // Add timeout to prevent hanging
      const timeoutId = setTimeout(() => {
        console.error('Xendit payment timeout after 30 seconds');
        resolve({
          error: { message: 'Payment timeout. Please try again.' },
          response: null,
        });
      }, 30000);

      window.Xendit.payment.collectCardData(cardData, (error: any, response: any) => {
        clearTimeout(timeoutId);
        // Additional safety check
        if (typeof window === 'undefined') {
          resolve({
            error: { message: 'Window context lost during payment processing' },
            response: null,
          });
          return;
        }
        
        resolve({ error, response });
      });
    } catch (err: any) {
        console.error('Error processing card payment with Xendit:', err);
      resolve({
        error: { message: err.message || 'Unknown error occurred' },
        response: null,
      });
    }
  });
};

/**
 * Clear and reset all Xendit session data and state
 * This function handles comprehensive cleanup of Xendit SDK state
 */
export const clearXenditSession = async (): Promise<boolean> => {
  return new Promise((resolve) => {
    try {
      if (typeof window === 'undefined' || !window.Xendit) {
        console.log('No Xendit to clear');
        resolve(true);
        return;
      }

      console.log('Starting comprehensive Xendit session cleanup...');
      
      // 1. Clear Cards Session if available
      if (window.Xendit.CardsSession) {
        try {
          // Clear Cards Session state
          if (typeof window.Xendit.CardsSession.destroy === 'function') {
            window.Xendit.CardsSession.destroy();
          }
          if (typeof window.Xendit.CardsSession.clear === 'function') {
            window.Xendit.CardsSession.clear();
          }
          console.log('✓ Cards Session cleared');
        } catch (err) {
          console.warn('Warning during Cards Session cleanup:', err);
        }
      }

      // 2. Clear payment data and state
      if (window.Xendit.payment) {
        try {
          // Clear payment session data
          if (typeof window.Xendit.payment.clear === 'function') {
            window.Xendit.payment.clear();
          }
          if (typeof window.Xendit.payment.reset === 'function') {
            window.Xendit.payment.reset();
          }
          console.log('✓ Payment session cleared');
        } catch (err) {
          console.warn('Warning during payment session cleanup:', err);
        }
      }

      // 3. Clear card data if available
      if (window.Xendit.card) {
        try {
          if (typeof window.Xendit.card.destroy === 'function') {
            window.Xendit.card.destroy();
          }
          if (typeof window.Xendit.card.clear === 'function') {
            window.Xendit.card.clear();
          }
          console.log('✓ Card data cleared');
        } catch (err) {
          console.warn('Warning during card data cleanup:', err);
        }
      }

      // 4. Clear device fingerprint if available
      if (window.Xendit.device && typeof window.Xendit.device.clear === 'function') {
        try {
          window.Xendit.device.clear();
          console.log('✓ Device fingerprint cleared');
        } catch (err) {
          console.warn('Warning during device fingerprint cleanup:', err);
        }
      }

      // 5. Reset authentication state
      if (window.Xendit.authentication) {
        try {
          if (typeof window.Xendit.authentication.reset === 'function') {
            window.Xendit.authentication.reset();
          }
          console.log('✓ Authentication state reset');
        } catch (err) {
          console.warn('Warning during authentication reset:', err);
        }
      }

      // 6. Clear any stored sessions or tokens
      try {
        // Clear sessionStorage entries related to Xendit
        if (typeof sessionStorage !== 'undefined') {
          const xenditKeys = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && (key.includes('xendit') || key.includes('Xendit'))) {
              xenditKeys.push(key);
            }
          }
          xenditKeys.forEach(key => {
            sessionStorage.removeItem(key);
          });
          if (xenditKeys.length > 0) {
            console.log(`✓ Cleared ${xenditKeys.length} sessionStorage entries`);
          }
        }
      } catch (err) {
        console.warn('Warning during sessionStorage cleanup:', err);
      }

      try {
        if (window.Xendit.settings && window.Xendit.settings.publishable_key) {
          console.log('✓ Global Xendit state preserved (publishable_key intact)');
        } else {
          console.warn('⚠️ Warning: publishable_key not found in settings');
        }
      } catch (err) {
        console.warn('Warning during global state check:', err);
      }
      resolve(true);
      
    } catch (error) {
      console.error('❌ Error during comprehensive Xendit session cleanup:', error);
      resolve(false);
    }
  });
};

/**
 * Reset Xendit SDK to initial state
 * This is more aggressive than clearXenditSession and will require re-initialization
 */
export const resetXenditSDK = async (): Promise<boolean> => {
  return new Promise(async (resolve) => {
    try {
      console.log('🔄 Performing complete Xendit SDK reset...');
      
      if (typeof window === 'undefined' || !window.Xendit) {
        console.log('No Xendit to reset');
        resolve(true);
        return;
      }
      
      // Preserve publishable_key BEFORE any operations
      const preservedPublishableKey = window.Xendit.settings?.publishable_key;
      
      try {
        // 1. Clear Cards Session if available
        if (window.Xendit.CardsSession) {
          if (typeof window.Xendit.CardsSession.destroy === 'function') {
            window.Xendit.CardsSession.destroy();
          }
        }

        // 2. Clear payment data
        if (window.Xendit.payment) {
          if (typeof window.Xendit.payment.clear === 'function') {
            window.Xendit.payment.clear();
          }
          if (typeof window.Xendit.payment.reset === 'function') {
            window.Xendit.payment.reset();
          }
        }

        // 3. Clear sessionStorage
        if (typeof sessionStorage !== 'undefined') {
          const xenditKeys = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && (key.includes('xendit') || key.includes('Xendit'))) {
              xenditKeys.push(key);
            }
          }
          xenditKeys.forEach(key => sessionStorage.removeItem(key));
        }
        
        // 4. DO NOT reset settings object or call _reset() 
        // These operations remove publishable_key causing /undefined/ in API URLs
        
        // 5. Ensure publishable_key is still set
        if (preservedPublishableKey && window.Xendit.setPublishableKey) {
          window.Xendit.setPublishableKey(preservedPublishableKey);
        }
        
      } catch (err) {
        console.warn('Warning during SDK reset:', err);
      }
      
      resolve(true);
      
    } catch (error) {
      console.error('❌ Error during complete SDK reset:', error);
      resolve(false);
    }
  });
};

/**
 * Format card number for display (add dashes)
 */
export const formatCardNumber = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\D/g, '');
  const formatted = cleaned.match(/.{1,4}/g)?.join('-') || '';
  return formatted;
};

/**
 * Validate card expiry date format (MM/YY)
 */
export const isValidExpiryDate = (expiryDate: string): boolean => {
  const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
  if (!regex.test(expiryDate)) {
    return false;
  }

  const [month, year] = expiryDate.split('/');
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear() % 100;
  const currentMonth = currentDate.getMonth() + 1;

  const expYear = parseInt(year, 10);
  const expMonth = parseInt(month, 10);

  if (expYear < currentYear) {
    return false;
  }

  if (expYear === currentYear && expMonth < currentMonth) {
    return false;
  }

  return true;
};

/**
 * Validate CVN/CVV
 */
export const isValidCVN = (cvn: string): boolean => {
  return /^\d{3,4}$/.test(cvn);
};

/**
 * Validate card number (basic Luhn algorithm)
 */
export const isValidCardNumber = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\D/g, '');
  
  if (cleaned.length < 13 || cleaned.length > 19) {
    return false;
  }

  // Luhn algorithm
  let sum = 0;
  let shouldDouble = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i), 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit = digit % 10 + 1;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};